import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Page } from '/imports/ui/Page';
import { PageBody } from '/imports/ui/PageBody';
import { PageHeading } from '/imports/ui/PageHeading';
import { Panel } from '/imports/ui/Panel';
import { Stat, StatRow } from '/imports/ui/Stat';
import { ChipGroup } from '/imports/ui/ChipGroup';
import { Badge } from '/imports/ui/Badge';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { useMethod, useUser } from '/imports/hooks';
import { getTraderStats } from '/imports/api/analytics';
import { useChartTheme } from '/imports/ui/chartTheme';

const RANGES = [
  { id: '90d', label: '90d', days: 90 },
  { id: '12m', label: '12m', days: 365 },
  { id: 'all', label: 'All', days: null as number | null },
];

const int = (n: number) => n.toLocaleString();
const pct = (n: number) => `${Math.round(n * 100)}%`;
const day = (d?: Date | null) => (d ? new Date(d).toISOString().slice(0, 10) : '--');

/**
 * Retention shading.
 *
 * Opacity, not a hue ramp: the grid is read by comparing cells down a column,
 * and a multi-hue scale makes neighbouring values look categorically different
 * when they differ by two percent.
 */
const cellTone = (value: number, size: number) => {
  if (!size) return 'bg-page-alt text-fg-3';
  const share = value / size;
  if (share >= 0.6) return 'bg-primary/40 text-fg-1';
  if (share >= 0.35) return 'bg-primary/25 text-fg-1';
  if (share >= 0.15) return 'bg-primary/15 text-fg-2';
  if (share > 0) return 'bg-primary/[0.07] text-fg-2';
  return 'bg-page-alt text-fg-3';
};

export const TradersPage = () => {
  const user = useUser();
  const [rangeId, setRangeId] = useState('all');
  const theme = useChartTheme(user?.settings?.theme);
  const range = RANGES.find((r) => r.id === rangeId) || RANGES[RANGES.length - 1];

  const arg = useMemo(() => {
    if (range.days == null) return {};
    const to = new Date();
    return { from: new Date(to.getTime() - range.days * 86400000), to };
  }, [range.days]);

  const [stats, loading] = useMethod(getTraderStats, arg, [rangeId]);

  if (!user?.isSuperUser) return <NotPermittedPage />;

  const widest = stats?.cohorts.reduce((n, c) => Math.max(n, c.retention.length), 0) ?? 0;

  return (
    <Page>
      <PageHeading
        left={<h1>Traders</h1>}
        right={<ChipGroup ariaLabel="Date range" chips={RANGES.map((r) => ({ id: r.id, label: r.label }))} value={rangeId} onChange={(id) => setRangeId(id)} />}
      />
      <PageBody>
        {loading || !stats ? <LoadingFlex /> : (
          <div className="flex flex-col gap-3 pb-6">
            <StatRow>
              <Stat label="Traders" value={int(stats.uniqueTraders)} icon="group" hint="distinct people in range" />
              <Stat
                label="New"
                value={int(stats.newTraders)}
                icon="person"
                hint="first ever trade in range"
              />
              <Stat label="Returning" value={int(stats.returningTraders)} icon="coPresent" hint="had traded before" />
              <Stat
                label="Repeat rate"
                value={pct(stats.repeatRate)}
                icon="percent"
                hint="traded more than once"
              />
            </StatRow>

            {/*
              The one metric the reference's Growth screen adds over this page.
              A first trade converting well below repeats points at the opening
              experience -- pricing, the greeting, or the bot wanting nothing
              from a newcomer's binder -- rather than at demand.
            */}
            <Panel eyebrow="Conversion" title="Does a first trade convert like a repeat?">
              {stats.firstTradeCompletion == null ? (
                <div className="py-8 text-center text-[13px] text-fg-3">Not enough trade history</div>
              ) : (
                <div className="flex flex-wrap items-end gap-8">
                  <div>
                    <div className="eyebrow text-fg-3">First trade</div>
                    <div className="font-mono text-[26px] leading-none font-semibold tabular-nums text-fg-1">{pct(stats.firstTradeCompletion)}</div>
                  </div>
                  <div>
                    <div className="eyebrow text-fg-3">Every later trade</div>
                    <div className="font-mono text-[26px] leading-none font-semibold tabular-nums text-fg-1">
                      {stats.repeatTradeCompletion == null ? '--' : pct(stats.repeatTradeCompletion)}
                    </div>
                  </div>
                  {stats.repeatTradeCompletion != null && (
                    <div className="pb-1 text-[13px] text-fg-2">
                      {(() => {
                        const gap = stats.firstTradeCompletion - stats.repeatTradeCompletion;
                        if (Math.abs(gap) < 0.03) return 'First trades convert about the same as repeats.';
                        return gap < 0
                          ? `First trades convert ${pct(Math.abs(gap))} worse than repeats.`
                          : `First trades convert ${pct(gap)} better than repeats.`;
                      })()}
                    </div>
                  )}
                </div>
              )}
            </Panel>

            {!stats.knownBotUids && (
              <Panel className="border-warn/40">
                <div className="text-[13px] text-fg-2">
                  <Badge tone="red">Unfiltered</Badge>
                  <span className="ml-2">No bot uids are known, so our own bots are being counted as traders and every figure here is inflated.</span>
                </div>
              </Panel>
            )}

            {/*
              "New" is measured against ALL history, not the selected range: a
              trader who last appeared in 2019 and came back today is returning,
              not new. Scoping first-seen to the window would relabel every
              dormant trader as an acquisition every time the range moved.
            */}
            <Panel
              eyebrow="Mix"
              title="New vs returning traders, by month"
              actions={<span className="text-[12px] text-fg-3">through {day(stats.lastActivityEver)}</span>}
            >
              {stats.series.length === 0 ? (
                <div className="py-14 text-center text-[13px] text-fg-3">No trades recorded</div>
              ) : (
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats.series} margin={{ top: 4, right: 8, bottom: 0, left: -14 }}>
                      <CartesianGrid stroke={theme.grid} vertical={false} />
                      <XAxis dataKey="bucket" tick={{ fill: theme.axis, fontSize: 11 }} tickLine={false} axisLine={false} minTickGap={24} />
                      <YAxis tick={{ fill: theme.axis, fontSize: 11 }} tickLine={false} axisLine={false} width={54} />
                      <Tooltip contentStyle={{ background: theme.panel, border: `1px solid ${theme.grid}`, borderRadius: 6, fontSize: 12, color: theme.text }} labelStyle={{ color: theme.axis, fontSize: 11 }} />
                      <Bar dataKey="returning" name="Returning" stackId="t" fill={theme.primary} />
                      <Bar dataKey="newTraders" name="New" stackId="t" fill={theme.tertiary} radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Panel>

            <div className="grid gap-3 lg:grid-cols-2">
              <Panel eyebrow="Loyalty" title="Most frequent traders">
                {stats.leaderboard.length === 0 ? (
                  <div className="py-10 text-center text-[13px] text-fg-3">No traders in range</div>
                ) : (
                  <div className="flex flex-col">
                    <div className="eyebrow grid grid-cols-[1.5fr_auto_auto_auto] gap-3 border-b border-soft pb-1.5 text-fg-3">
                      <span>Trader</span><span className="text-right">Trades</span><span className="text-right">Done</span><span className="text-right">Since</span>
                    </div>
                    {stats.leaderboard.map((t) => (
                      <div key={t.partnerId} className="grid grid-cols-[1.5fr_auto_auto_auto] items-center gap-3 border-b border-soft py-1.5 text-[13px] last:border-0">
                        <Link
                          to={`/trade-inspector?partner=${t.partnerId}&name=${encodeURIComponent(t.partner)}`}
                          className="truncate text-fg-1 no-underline hover:underline"
                          title={`last seen ${day(t.lastSeen)} — open their trades`}
                        >
                          {t.partner}
                        </Link>
                        <span className="text-right font-mono tabular-nums text-fg-2">{int(t.trades)}</span>
                        <span className="text-right font-mono tabular-nums text-fg-3">{t.trades ? pct(t.completed / t.trades) : '--'}</span>
                        <span className="text-right font-mono tabular-nums text-fg-3">{day(t.firstSeen).slice(0, 7)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Panel>

              {/*
                Rows are ragged on purpose. A cohort from last month has no
                six-month figure yet, and padding it with 0 would read as total
                churn rather than as not-yet-knowable.
              */}
              <Panel eyebrow="Retention" title="Cohorts by first trade" actions={<span className="text-[12px] text-fg-3">months after joining</span>}>
                {stats.cohorts.length === 0 ? (
                  <div className="py-10 text-center text-[13px] text-fg-3">Not enough history</div>
                ) : (
                  <div className="overflow-x-auto">
                    <div className="min-w-[420px]">
                      <div className="eyebrow flex gap-1 border-b border-soft pb-1.5 text-fg-3">
                        <span className="w-16 shrink-0">Cohort</span>
                        <span className="w-10 shrink-0 text-right">Size</span>
                        {Array.from({ length: widest }, (_, i) => (
                          <span key={i} className="w-9 shrink-0 text-center">+{i}</span>
                        ))}
                      </div>
                      {stats.cohorts.map((c) => (
                        <div key={c.cohort} className="flex items-center gap-1 py-1 text-[12px]">
                          <span className="w-16 shrink-0 font-mono tabular-nums text-fg-2">{c.cohort}</span>
                          <span className="w-10 shrink-0 text-right font-mono tabular-nums text-fg-1">{int(c.size)}</span>
                          {c.retention.map((v, i) => (
                            <span
                              key={i}
                              title={`${v} of ${c.size} active ${i} month${i === 1 ? '' : 's'} later`}
                              className={`w-9 shrink-0 rounded-sm py-1 text-center font-mono tabular-nums ${cellTone(v, c.size)}`}
                            >
                              {v}
                            </span>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Panel>
            </div>
          </div>
        )}
      </PageBody>
    </Page>
  );
};
